/**
 * ExternalViewPage — token-based view for external clients.
 *
 * Accessible at /ext/:token — shows branded login for unauthenticated users,
 * then the view panels once authenticated.
 *
 * planTEK Wave 1 — External Views public access.
 * planTEK Phase 10d.4 — Branded portal entry experience.
 */
import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { injectCSSVariables, DEFAULT_THEME } from '@/lib/theme';
import { useAuth } from '@/lib/auth/AuthProvider.tsx';
import { Skeleton } from '@/components/ui/skeleton';
import { PoweredByFooter } from '@/components/external/PoweredByFooter';
import { ExternalViewLayout, PanelGrid } from '@/components/external/ExternalViewLayout';
import { PanelRenderer } from '@/components/external/PanelRenderer';

function usePublicView(token: string | undefined, password?: string) {
  return useQuery({
    queryKey: ['ext-view', token, password],
    queryFn: () => api.externalViews.getPublic(token!, password),
    enabled: !!token,
    retry: false,
  });
}

export function ExternalViewPage() {
  const { token: pathToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  // Support both /ext/:token (path) and /ext?token=abc (query param)
  const token = pathToken ?? searchParams.get('token') ?? undefined;
  const [submittedPassword, setSubmittedPassword] = useState<string | undefined>();
  const [passwordInput, setPasswordInput] = useState('');
  // Track theme injection to prevent flash-of-unstyled-content (FOUC).
  // The skeleton remains until both theme and view data are ready.
  const [themeReady, setThemeReady] = useState(false);
  // Active view ID for multi-view switcher
  const [activeViewId, setActiveViewId] = useState<string | undefined>();

  const location = useLocation();
  // /portal/:token is a public share link — no login required.
  // /ext/:token requires auth and redirects unauthenticated users to branded login.
  const isPublicPortal = location.pathname.startsWith('/portal/');

  const { isAuthenticated, isLoading: authLoading, user, signOut } = useAuth();
  const { data, isLoading, error } = usePublicView(token, submittedPassword);

  // Remember the last viewed token so PortalLoginPage can redirect back here after login.
  useEffect(() => {
    if (token && isAuthenticated) {
      localStorage.setItem('portal_last_view', token);
    }
  }, [token, isAuthenticated]);

  // Fetch and inject tenant theme before revealing panels.
  // For public portal: fetch theme even without auth (best-effort).
  // For /ext/: unauthenticated users are redirected below; ExternalLoginPage handles theme.
  useEffect(() => {
    if (!token || (!isAuthenticated && !isPublicPortal)) {
      // No theme to fetch — mark ready so render can proceed (redirect or empty).
      setThemeReady(true);
      return;
    }
    api.externalViews.getPublicMeta(token).then((meta) => {
      if (meta?.theme) {
        injectCSSVariables(meta.theme);
      }
    }).catch(() => {
      injectCSSVariables(DEFAULT_THEME);
    }).finally(() => {
      setThemeReady(true);
    });
  }, [token, isAuthenticated, isPublicPortal]);

  const err = error as any;
  const requiresPassword = err?.status === 401 && err?.data?.['X-View-Password-Required'] === 'true'
    || (err?.status === 401);

  // Phase 10d.4: Redirect unauthenticated users to the branded login page.
  // Only applies to /ext/:token — /portal/:token is always public (no auth needed).
  if (!isPublicPortal && !authLoading && !isAuthenticated && token) {
    return <Navigate to={`/ext/${token}/login`} replace />;
  }

  if (isLoading || !themeReady || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-3xl space-y-4">
          <Skeleton className="h-10 w-1/2" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (err && !requiresPassword) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-6 max-w-md text-center">
          <h1 className="font-semibold text-lg mb-2">View Unavailable</h1>
          <p className="text-sm text-muted-foreground">
            {err?.data?.detail || 'This view is not available. It may have expired or been deactivated.'}
          </p>
        </div>
        <PoweredByFooter />
      </div>
    );
  }

  if (requiresPassword && !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="rounded-lg border bg-card p-6 max-w-sm w-full shadow-sm">
          <h1 className="font-semibold text-lg mb-1">Password Required</h1>
          <p className="text-sm text-muted-foreground mb-4">
            This view is password protected. Enter the password to continue.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setSubmittedPassword(passwordInput);
            }}
            className="space-y-3"
          >
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Enter password"
              className="w-full rounded-md border px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
            <button
              type="submit"
              className="w-full rounded-md bg-primary px-3 py-2.5 min-h-[44px] text-sm text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Submit
            </button>
          </form>
        </div>
        <PoweredByFooter />
      </div>
    );
  }

  // Normalize response — backend may return nested {view:{...}} (current)
  // or flat {view_name, branding, panels} (deployed staging version).
  const view = data?.view ?? (data?.view_name ? {
    id: data.view_id ?? '',
    name: data.view_name,
    panels: data.panels ?? [],
    branding: data.branding ?? null,
    layout: data.layout ?? null,
    welcome_message: data.welcome_message ?? null,
  } : null);
  if (!view) return null;

  // Multi-view support: if the response includes additional assigned views,
  // use them for the view switcher. The primary view is always in data.view.
  const allViews: any[] = data?.views?.length > 0
    ? data.views
    : [view];

  const displayedView = activeViewId && activeViewId !== view.id
    ? allViews.find((v: any) => v.id === activeViewId) ?? view
    : view;

  const columns = displayedView.layout?.columns ?? 'auto';

  const portalUser = user
    ? { name: user.name, email: user.email, avatar_url: user.photoURL }
    : undefined;

  // Phase 10e: Read auto-accept params from URL query string (set by acceptance email links)
  const autoAcceptActionId = searchParams.get('accept_action') ?? undefined;
  const autoAcceptDeliverableId = searchParams.get('accept_deliverable') ?? undefined;

  return (
    <ExternalViewLayout
      view={displayedView}
      views={allViews}
      onViewSwitch={setActiveViewId}
      user={portalUser}
      onLogout={signOut}
    >
      <PanelGrid columns={columns}>
        <PanelRenderer
          panels={[...(displayedView.panels ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))}
          token={token}
          userEmail={user?.email ?? ''}
          autoAcceptActionId={autoAcceptActionId}
          autoAcceptDeliverableId={autoAcceptDeliverableId}
        />
      </PanelGrid>
    </ExternalViewLayout>
  );
}
